// The member view (docs/mastermind-admin-plan.md 3.3, and the critique's build plan of 20 Oct, P3): 1 member of a
// mastermind, with the head (their name, group, membership month and where they stand) and 1 More menu (Person page,
// View as, Change group, Pause until a date and Guarantee check), the status line when their access isn't active,
// Previous and Next through the list Adam came from (always in name order), and 2 tabs, This month and Accountability
// log, each with its own address. This month is 2 columns on a laptop (This month and Earlier months; Needs you,
// Sessions, Goal, Wins, Guarantee check and History of changes) and 1 on a phone, where This month comes first when a
// review is due and the other cards fold to a line, with the stepper as a bar at the bottom. 1 read for the member
// (hub.accountability_log, the staff version), with the members, the product's sessions and groups beside it, kept as
// Adam steps from 1 member to the next, and their access rows for the status line. Everything else is the member's
// own: a step to the next member waits for their log (never drawing 1 member's log under another's address) and
// starts every form afresh, and the line a write answered with stays with the member it was about.
//
// Closing a month is held for 10 seconds with Undo (the critique's admin 8): hub.close_month is sent when the 10
// seconds end, or at once when Adam steps on to another member, closes the next member's month or leaves the page.
// "Close month 2 and go to Tom Reid" goes to Tom straight away, and the hold runs there, naming Sam (drawn while Tom's
// log loads too); Undo brings Adam back to Sam's review as it was, as a close refused after he's gone on does. View as
// and the Person page wait for the held close first, since neither could say how it went (and nothing can be changed
// while viewing as). While a close is held, closing or reloading the tab asks Adam to stay, and if the page goes anyway
// (or is hidden on a phone, which may never come back to it) the close is sent with keepalive, which outlives the page.
//
// Migration 80 (docs/accelerator-80-spec.md 4.8): owner and admin can set a member's level by hand from More (Set the
// level by hand, or Change the level), and the head then says it beside the automatic level, with the note and Back to
// automatic; This month says the session the open month is set at, with Change. Both draw only when the staff log has
// level_set, which only 80 gives, so on a database without 80 the page is as it was.
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, navigate } from '../../lib/router.jsx'
import { setTitle } from '../../config/site.js'
import { usePhone } from '../../lib/media.js'
import { Button, Card, Crumbs, Field, Loading, Problem, Select, Tabs, ViewAsButton, adminPath, useAdmin, useLoad } from '../ui/index.js'
import { MarkIcon } from '../../products/mastermind/Log.jsx'
import { CHANGE } from '../people/words.js'
import { ChangeForm, changeItems } from '../people/AccessCard.jsx'
import { MM, PANEL, sessionChoices } from './words.js'
import { closeMonth, closeMonthAsPageGoes, loadAccessFor, loadGroups, loadLog, loadMembers, loadSessions, moveGroup } from './data.js'
import { goalFact, headLine, levelHead, measureStrip, memberRow, needsList, pickRows } from './section.js'
import { StandMark } from './Members.jsx'
import { accountability } from '../../products/mastermind/log.js'
import { ThisMonth, Said } from './ThisMonth.jsx'
import { EarlierMonths } from './EarlierMonths.jsx'
import { SessionsCard } from './SessionsCard.jsx'
// The register box on This month (Adam, 18 Oct): the session picked stays as he steps from 1 member to the next
import { RegisterBox } from './RegisterBox.jsx'
import { GoalSection, WinsSection } from './Panel.jsx'
import { TermCard } from './TermCard.jsx'
import { ChangesCard } from './ChangesCard.jsx'
import { LogTab } from './LogTab.jsx'
import { StatusLine } from './StatusLine.jsx'
import { PhoneFold, openAt } from './Fold.jsx'
import { BackToAuto, LevelForm } from './LevelForm.jsx'

const STRIP_TARGET = { sessions: '#mm-sessions', actions: '#mm-earlier', goal: '#mm-goal', membership: '#mm-term' }
const HOLD_MS = 10000

function ChangeGroup({ log, groups, first, onDone, onCancel }) {
  const { api } = useAdmin()
  const [value, setValue] = useState(log.access?.cohort_id || '')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState(null)
  async function go() {
    if (busy) return
    setBusy(true); setProblem(null)
    try {
      await moveGroup(api, { accessId: log.access.access_id, cohortId: value || null })
      await onDone(MM.moved(first, groups.find(g => g.id === value)?.name || null))
    } catch (e) { setProblem(e.message) } finally { setBusy(false) }
  }
  return (
    <div className="amx-form amx-inline" role="group" aria-label={MM.changeGroup}>
      <Field label={MM.groupField} plain error={problem}>
        {props => <Select {...props} value={value} onChange={setValue} options={[...groups.map(g => ({ value: g.id, label: g.name })), { value: '', label: MM.noGroupOption }]} />}
      </Field>
      <span className="amx-row-actions">
        <Button kind="ink" size="sm" busy={busy} disabled={value === (log.access?.cohort_id || '')} onClick={go}>{MM.moveGroup}</Button>
        <Button size="sm" disabled={busy} onClick={onCancel}>{MM.cancelReview}</Button>
      </span>
    </div>
  )
}

// The head's 1 More menu: its items close it as they're picked
function MoreMenu({ label, children }) {
  const [open, setOpen] = useState(false)
  const box = useRef(null)
  const button = useRef(null)
  useEffect(() => {
    if (!open) return undefined
    const onKey = e => { if (e.key === 'Escape') { setOpen(false); button.current?.focus() } }
    const onDown = e => { if (box.current && !box.current.contains(e.target)) setOpen(false) }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown) }
  }, [open])
  return (
    <span className="amx-menu" ref={box}>
      <button ref={button} type="button" className="ad-btn ad-btn--line ad-btn--sm" aria-haspopup="true" aria-expanded={open}
        aria-label={label} onClick={() => setOpen(o => !o)}>{MM.more}</button>
      {open && <span className="amx-menu-list" role="menu" aria-label={label}>{children(() => setOpen(false))}</span>}
    </span>
  )
}

export function MemberView({ product, personId, tab, role, query }) {
  const { api, personId: me } = useAdmin()
  const phone = usePhone()
  const oa = role === 'owner' || role === 'admin'
  const side = useLoad(async () => {
    const [members, sessions, groups] = await Promise.all([loadMembers(api, product.id), loadSessions(api, product.id), loadGroups(api, product.id)])
    return { members: members || [], sessions: sessions || [], groups: groups || [] }
  }, [api, product.id])
  const page = useLoad(() => loadLog(api, personId, product.id), [api, personId, product.id])
  // their access rows and billing, for the status line and Pause until a date (the person page's reads)
  const access = useLoad(() => loadAccessFor(api, personId), [api, personId])
  // { text, person }: shown only on that member's view
  const [said, setSaid] = useState(null)
  const setLine = (l, person = personId) => setSaid(l ? { ...l, person } : null)
  const line = said && said.person === personId ? said : null
  // the register box's session: kept here, so Next and Previous keep it
  const [regSession, setRegSession] = useState(null)
  // a form opened from the head: 'group', 'level' (the level set by hand), 'auto' (back to automatic), or a Change form
  // of the access row ('pause', 'resume')
  const [form, setForm] = useState(null)
  useEffect(() => { setForm(null) }, [personId])
  // the log read for this member: while the next member's loads, the last one's is never drawn under their address
  const log = page.data && page.data.person?.person_id === personId ? page.data : null

  // the query every link here keeps as Adam steps on (his group, the filter, the review), without the list's send=1,
  // which opens the Send box only on the log it was sent from
  const keep = new URLSearchParams(query)
  keep.delete('send')
  const qs = keep.toString() ? `?${keep}` : ''
  const rows = useMemo(() => (side.data?.members || []).map(m => memberRow(m, { me })), [side.data, me])
  const group = query.get('group') || 'all'
  const show = query.get('show') || 'all'
  // Previous and Next always step in name order, whatever order the list was in (the critique's admin 9)
  const list = pickRows(rows, { group, show, order: 'name' })
  const at = list.findIndex(r => r.id === personId)
  // reviewing, the member just closed drops out of the list: the next is the 1 after them by name
  const nextRow = at >= 0 ? list[at + 1] || null : list.find(r => r.name.localeCompare(log?.person?.full_name || '', 'en-GB') > 0) || null
  const prevRow = at > 0 ? list[at - 1] : null
  // the tab Adam is stepping through, by its name, and the way back to it: Everyone's is Everyone, never the member's
  // own group
  const groupName = group === 'all' ? MM.everyoneName : side.data?.groups.find(g => g.id === group)?.name || null
  const listPath = `${adminPath.mastermind(product.slug)}?group=${group}`

  // ---- the 10-second hold on closing a month ----
  const [hold, setHold] = useState(null)
  const holdRef = useRef(null)
  const timer = useRef(null)
  const here = useRef(personId)
  const [restore, setRestore] = useState(null) // { person, draft }: the review as it was, after Undo
  const [closeProblem, setCloseProblem] = useState(null) // { person, text }
  const reload = useRef(null)
  reload.current = async () => { await Promise.all([page.reload(), side.reload()]) }
  const sendRef = useRef(null)
  // sends a held close now: true once it's closed, false if it was refused (said on the member's review, or here when
  // Adam has gone on, with the review kept as it was for when he goes back). leaving: the page is going, so with keepalive
  sendRef.current = async (entry, { leaving = false } = {}) => {
    if (!entry || holdRef.current !== entry) return true
    clearTimeout(timer.current)
    timer.current = null
    holdRef.current = null
    setHold(null)
    let ok = true
    try {
      const answer = await (leaving && entry.token
        ? closeMonthAsPageGoes(api, entry.payload, entry.ctx, entry.token)
        : closeMonth(api, entry.payload, entry.ctx))
      setSaid({
        person: here.current,
        text: MM.closed({
          first: entry.first, month: answer.month, done: answer.done, part: answer.part_done, not: answer.not_done, carried: answer.carried,
          next: answer.next_month, total: (answer.carried || 0) + (answer.added || 0),
        }),
      })
    } catch (e) {
      ok = false
      if (here.current === entry.person) setCloseProblem({ person: entry.person, text: e.message })
      else {
        setSaid({ person: here.current, bad: true, text: MM.closeFailed(entry.first, entry.month, e.message) })
        setRestore({ person: entry.person, draft: entry.draft })
      }
    }
    await reload.current()
    return ok
  }
  // a held close goes at once, and is waited for, before Adam leaves for a page that couldn't say how it went: false
  // if it was refused, so he stays and reads why
  const flushHold = async () => (holdRef.current ? sendRef.current(holdRef.current) : true)
  // stepping on to another member sends a held close at once, as leaving the page does
  useEffect(() => {
    here.current = personId
    const h = holdRef.current
    if (h && personId !== (h.landed || h.person)) sendRef.current(h)
    // the review as it was before an Undo is for that member's page only
    setRestore(r => (r && r.person !== personId ? null : r))
  }, [personId])
  useEffect(() => {
    const onGo = () => { if (holdRef.current) sendRef.current(holdRef.current, { leaving: true }) }
    const onHidden = () => { if (document.visibilityState === 'hidden') onGo() }
    window.addEventListener('pagehide', onGo)
    document.addEventListener('visibilitychange', onHidden)
    return () => {
      window.removeEventListener('pagehide', onGo)
      document.removeEventListener('visibilitychange', onHidden)
      if (holdRef.current) sendRef.current(holdRef.current)
    }
  }, [])
  // while a close is held, closing or reloading the tab asks Adam to stay (a phone's browser may not ask: the close
  // then goes with keepalive, above)
  useEffect(() => {
    if (!hold) return undefined
    const onLeave = e => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', onLeave)
    return () => window.removeEventListener('beforeunload', onLeave)
  }, [hold])
  function undo() {
    const h = holdRef.current
    if (!h) return
    clearTimeout(timer.current)
    timer.current = null
    holdRef.current = null
    setHold(null)
    setRestore({ person: h.person, draft: h.draft })
    setSaid({ person: h.person, text: MM.undone(h.month) })
    if (personId !== h.person) navigate(`${adminPath.mmMember(product.slug, h.person)}${qs}`)
  }
  // the hold and its Undo, drawn while the next member's log loads too, so none of the 10 seconds goes without it
  const holdBar = hold && (
    <div className="amx-hold" role="status">
      <span className="amx-hold-text">{hold.person === personId ? MM.closing(hold.month) : MM.closingFor(hold.first, hold.month)}</span>
      <Button size="sm" onClick={undo}>{MM.undo}</Button>
    </div>
  )

  if (!log) {
    if (page.status === 'error') return <Problem error={page.error} onRetry={page.reload} />
    if (page.status === 'loading' || page.data) return <>{holdBar}<Loading /></>
    return (
      <>
        <Crumbs items={[{ label: MM.eyebrow, to: adminPath.masterminds() }, { label: product.name, to: adminPath.mastermind(product.slug) }, { label: MM.nothingHere }]} />
        <Card title={MM.nothingHere}><p className="ad-lede">{MM.nothingLine}</p></Card>
      </>
    )
  }

  const first = log.person.first_name || (log.person.full_name || '').split(' ')[0]
  const person = { id: personId, full_name: log.person.full_name, first_name: log.person.first_name, role: null }
  const ctx = { first, product: product.name, group: log.access?.cohort_name || 'the group' }
  const sum = log.summary || {}
  // where they stand on their accountability, worked out from the log exactly as the log tab draws it (levels.js), with
  // a level set by hand over it; head: what the head says of a level set by hand, or null
  const stand = accountability(log).level
  const head = levelHead(log, me)
  // migration 80 is on the database: the staff log always has level_set then (null with none set)
  const has80 = Object.prototype.hasOwnProperty.call(log, 'level_set')
  const canSet = oa && has80
  const chip = MM.access[log.access?.status] || null
  const strip = measureStrip(log)
  const choices = sessionChoices(side.data?.sessions || [], { cohortId: log.access?.cohort_id, cohorts: side.data?.groups || [], nowMs: Date.now() })
  const changed = async () => { await Promise.all([page.reload(), side.reload(), access.reload()]) }
  const tabPath = t => `${t === 'log' ? adminPath.mmLog(product.slug, personId) : adminPath.mmMember(product.slug, personId)}${qs}`
  const memberPath = r => `${tab === 'log' ? adminPath.mmLog(product.slug, r.id) : adminPath.mmMember(product.slug, r.id)}${qs}`
  if (tab !== 'log') setTitle(`Admin: ${log.person.full_name}`)

  // the access row of this mastermind, as the person page reads it, or what the log knows of it
  const rows2 = access.data?.access || []
  const row = rows2.find(a => a.id === log.access?.access_id) || (log.access?.access_id ? { id: log.access.access_id, status: log.access.status, product_id: product.id, cohort_id: log.access.cohort_id } : null)
  const billing = row ? (access.data?.billing || []).find(b => b.access_id === row.id) : null
  const canPause = oa && row && changeItems(row, { cohorts: [], billing, hasCustomer: access.data?.hasCustomer, product }).some(([k]) => k === 'pause')

  function startHold({ how, payload, draft, month }) {
    const pending = holdRef.current
    if (pending && pending.person === personId) return
    const landed = how === 'next' && nextRow ? nextRow.id : null
    const entry = { person: personId, first, month, payload, ctx, draft, landed, token: null }
    // another member's close still held (Sam's, as Adam closes Tom's straight after): it goes now, so both do
    if (pending) sendRef.current(pending)
    holdRef.current = entry
    setHold(entry)
    setCloseProblem(null)
    setRestore(null)
    setSaid(null)
    timer.current = setTimeout(() => sendRef.current(entry), HOLD_MS)
    // the session's token now, for a close sent as the page goes
    api.db.auth.getSession().then(({ data }) => { entry.token = data?.session?.access_token || null }, () => {})
    if (landed) navigate(`${adminPath.mmMember(product.slug, landed)}${qs}`)
  }

  const needs = needsList(log, first)
  const reviewDue = Boolean(sum.flags?.review_due)
  const reviewing = show === 'review'
  const position = reviewing ? MM.positionReview(at + 1, list.length) : MM.position(at + 1, list.length, groupName)
  const stepper = (prevRow || nextRow || at >= 0) && (
    <nav className="amx-stepper" aria-label={position}>
      {prevRow
        ? <Link className="amx-step amx-step--prev" to={memberPath(prevRow)} aria-label={MM.prev(prevRow.name)}><span className="amx-chev amx-chev--prev" aria-hidden="true" /><span className="amx-step-name">{prevRow.first}</span></Link>
        : <span className="amx-step amx-step--none" />}
      {at >= 0 && <span className="amx-step-pos">{position}</span>}
      {nextRow
        ? <Link className="amx-step amx-step--next" to={memberPath(nextRow)} aria-label={MM.next(nextRow.name)}><span className="amx-step-name">{nextRow.first}</span><span className="amx-chev amx-chev--next" aria-hidden="true" /></Link>
        : at >= 0
          ? <Link className="amx-step amx-step--next amx-step--done" to={listPath}>{MM.backTo2(groupName)}</Link>
          : <span className="amx-step amx-step--none" />}
    </nav>
  )
  const backTo = window.location.pathname + qs
  const shared = (log.wins || []).filter(w => w.shareable).length
  const closedMonths = (log.months || []).filter(m => m.closed_at).length
  const crumbGroup = log.access?.cohort_name

  return (
    <div className="amx-member-view" key={personId}>
      {phone
        ? (
          <nav className="ad-crumbs amx-crumb-back" aria-label="Where you are">
            <Link to={log.access?.cohort_id ? `${adminPath.mastermind(product.slug)}?group=${log.access.cohort_id}` : adminPath.mastermind(product.slug)}>
              <span className="amx-chev amx-chev--prev" aria-hidden="true" />{MM.backToGroup(crumbGroup)}
            </Link>
          </nav>
        )
        : (
          <Crumbs items={[
            { label: MM.eyebrow, to: adminPath.masterminds() },
            { label: product.name, to: adminPath.mastermind(product.slug) },
            ...(crumbGroup ? [{ label: crumbGroup, to: `${adminPath.mastermind(product.slug)}?group=${log.access.cohort_id}` }] : []),
            { label: log.person.full_name },
          ]} />
        )}
      <section className="amx-head">
        <div className="amx-head-text">
          <h1 className="ad-h1">{log.person.full_name}</h1>
          <p className="amx-head-line">{headLine(log)}</p>
          <p className="amx-head-stand">
            {chip && <span className="ad-tag amx-chip">{chip}</span>}
            <StandMark state={stand.key}>{head ? head.line : MM.levelLine(stand)}</StandMark>
          </p>
          {head?.note && <p className="amx-level-note">{head.note}</p>}
          {head && canSet && (
            <span className="amx-level-back"><Button kind="quiet" size="sm" onClick={() => setForm('auto')}>{MM.backToAuto}</Button></span>
          )}
        </div>
        <div className="amx-head-actions">
          <MoreMenu label={MM.moreFor(first)}>
            {close => (
              <>
                <Link role="menuitem" className="amx-menu-item" to={adminPath.person(personId)} onClick={async e => {
                  close()
                  if (!holdRef.current) return
                  e.preventDefault()
                  if (await flushHold()) navigate(adminPath.person(personId))
                }}>{MM.personPage}</Link>
                <ViewAsButton person={person} kind="menu" itemClass="amx-menu-item" beforeStart={flushHold}
                  to={tab === 'log' ? `${product.path}/accountability-log` : product.path} back={backTo} />
                {oa && log.access?.access_id && <button type="button" role="menuitem" className="amx-menu-item" onClick={() => { close(); setForm('group') }}>{MM.changeGroup}</button>}
                {canSet && <button type="button" role="menuitem" className="amx-menu-item" onClick={() => { close(); setForm('level') }}>{log.level_set ? MM.changeLevel : MM.setLevel}</button>}
                {canPause && <button type="button" role="menuitem" className="amx-menu-item" onClick={() => { close(); setForm('pause') }}>{CHANGE.pause}</button>}
                {tab !== 'log' && (
                  <a role="menuitem" className="amx-menu-item" href="#mm-term" onClick={() => { close(); openAt('#mm-term') }}>{MM.termOpen}</a>
                )}
              </>
            )}
          </MoreMenu>
        </div>
      </section>
      {row && (
        <StatusLine api={api} row={row} billing={billing} hasCustomer={access.data?.hasCustomer} sum={sum} personId={personId} first={first}
          onOpen={kind => setForm(kind)} onDone={async text => { await changed(); setLine({ text }) }} />
      )}
      {form === 'group' && (
        <ChangeGroup log={log} groups={side.data?.groups || []} first={first} onCancel={() => setForm(null)}
          onDone={async text => { setForm(null); await changed(); setLine({ text }) }} />
      )}
      {form === 'level' && canSet && (
        <LevelForm log={log} first={first} person={personId} product={product.id} ctx={ctx} onCancel={() => setForm(null)}
          onDone={async text => { setForm(null); await changed(); setLine({ text }) }} />
      )}
      {form === 'auto' && canSet && log.level_set && (
        <BackToAuto log={log} first={first} person={personId} product={product.id} ctx={ctx} onClose={() => setForm(null)}
          onDone={async text => { setForm(null); await changed(); setLine({ text }) }} />
      )}
      {(form === 'pause' || form === 'resume') && row && (
        <ChangeForm kind={form} row={row} product={product} cohorts={side.data?.groups || []} first={first}
          onCancel={() => setForm(null)} onDone={async message => { setForm(null); await changed(); setLine({ text: message }) }} />
      )}
      {holdBar}
      <Said line={line} />
      {stepper}
      <Tabs label={log.person.full_name} current={tab === 'log' ? 'log' : 'month'} items={[
        { key: 'month', label: MM.tabs.month, to: tabPath('month') },
        { key: 'log', label: MM.tabs.log, to: tabPath('log') },
      ]} />

      {tab === 'log'
        ? <LogTab log={log} product={product} first={first} query={query} onChanged={changed} sessions={side.data?.sessions || null} />
        : (
          <div className={`amx-month-tab${reviewDue ? ' amx-month-tab--review' : ''}`}>
            <div className="amx-reg-slot">
              <RegisterBox log={log} person={personId} first={first} sessions={side.data?.sessions || []} picked={regSession} onPick={setRegSession} onChanged={changed} />
            </div>
            <ul className="amx-strip" aria-label={MM.tabs.month}>
              {strip.map(s => (
                <li key={s.key}><a href={STRIP_TARGET[s.key]} onClick={() => openAt(STRIP_TARGET[s.key])} className={`amx-strip-item amx-strip-item--${s.mark || 'fact'}`}>{s.mark && <MarkIcon mark={s.mark} />}{s.text}</a></li>
              ))}
            </ul>
            <div className="amx-cols">
              <div className="amx-col amx-col--main">
                <div className="amx-o1">
                  <ThisMonth log={log} person={personId} product={product.id} first={first} choices={choices} next={nextRow} ctx={ctx} has80={canSet}
                    held={hold?.person === personId} draft={restore?.person === personId ? restore.draft : null}
                    problem={closeProblem?.person === personId ? closeProblem.text : null}
                    onChanged={changed} onClose={startHold} />
                </div>
                <div className="amx-o6"><PhoneFold title={MM.earlierH} line={MM.earlierCount(closedMonths)}><EarlierMonths log={log} first={first} ctx={ctx} onChanged={changed} /></PhoneFold></div>
              </div>
              <div className="amx-col amx-col--side">
                {needs.length > 0 && (
                  <div className="amx-o2">
                    <Card id="mm-needs" title={MM.needsH} className="amx-needs-card">
                      <ul className="amx-needs-list">{needs.map(n => <li key={n.key}>{n.target ? <a href={n.target} onClick={() => openAt(n.target)}>{n.text}</a> : n.text}</li>)}</ul>
                    </Card>
                  </div>
                )}
                <div className="amx-o3"><PhoneFold title={MM.sessionsH} line={strip[0].text}><SessionsCard log={log} person={personId} first={first} ctx={ctx} onChanged={changed} /></PhoneFold></div>
                <div className="amx-o4"><PhoneFold title={PANEL.goal} line={goalFact(sum.goal)}><GoalSection log={log} person={personId} first={first} product={product.id} oa={oa} ctx={ctx} onChanged={changed} /></PhoneFold></div>
                <div className="amx-o5"><PhoneFold title={PANEL.wins} line={PANEL.winsCount((log.wins || []).length, shared) || PANEL.noWins(first)}><WinsSection log={log} person={personId} first={first} product={product.id} oa={oa} ctx={ctx} onChanged={changed} /></PhoneFold></div>
                <div className="amx-o7" id="mm-term"><TermCard log={log} /></div>
                <div className="amx-o8"><ChangesCard log={log} first={first} /></div>
              </div>
            </div>
          </div>
        )}
    </div>
  )
}
